import {
  posesFor,
  type FurnitureWire,
  type MovesWire,
  type PieceInfo,
  type PlacementSpace,
  type PosePayload,
} from '@vrlearning/lesson-schema';
import type { GrabAnswer } from './furnishing.js';

/**
 * Where the furniture of a furnish step comes from and goes to (ADR-037): in a class the session
 * (the server decides who holds what), outside one -- practising alone, `?play=`, the desktop
 * preview -- a `LocalFurnishing` that grants every grab at once and keeps nothing. The villa's
 * interaction (render/furniture/interaction.ts) sees only this.
 */
export interface FurnishingLink {
  /** How the furniture messages name this headset; undefined until known. */
  readonly self: string | undefined;
  grab(pieceId: string): Promise<GrabAnswer>;
  hold(pose: PosePayload): void;
  drop(pose: PosePayload): void;
  /** Every piece where it stands and who holds it; the latest at once to a new listener. */
  onFurniture(handler: (wire: FurnitureWire) => void): void;
  /** Pieces other headsets carry, batched. */
  onMoves(handler: (moves: MovesWire) => void): void;
  dispose(): void;
}

/** What the link needs of the session client (api/session-client.ts). */
export type LinkClient = {
  readonly deviceId: string | undefined;
  readonly furniture: FurnitureWire | undefined;
  grab(pieceId: string): Promise<GrabAnswer>;
  hold(pose: PosePayload): void;
  drop(pose: PosePayload): void;
  onFurniture(handler: (wire: FurnitureWire) => void): void;
  onMoves(handler: (moves: MovesWire) => void): void;
};

/** The class's furniture, through the session client. One lesson at a time listens. */
export function sessionLink(client: LinkClient): FurnishingLink {
  const furnitureHandlers = new Set<(wire: FurnitureWire) => void>();
  const movesHandlers = new Set<(moves: MovesWire) => void>();
  let live = true;
  client.onFurniture((wire) => {
    if (live) for (const handler of furnitureHandlers) handler(wire);
  });
  client.onMoves((moves) => {
    if (live) for (const handler of movesHandlers) handler(moves);
  });
  return {
    get self() {
      return client.deviceId;
    },
    grab: (pieceId) => client.grab(pieceId),
    hold: (pose) => client.hold(pose),
    drop: (pose) => client.drop(pose),
    onFurniture(handler) {
      furnitureHandlers.add(handler);
      const last = client.furniture;
      if (last) handler(last);
    },
    onMoves(handler) {
      movesHandlers.add(handler);
    },
    dispose() {
      live = false;
      furnitureHandlers.clear();
      movesHandlers.clear();
    },
  };
}

/**
 * Furnishing alone: every piece starts in its start slot (or where `saved` has it), every grab is
 * granted at once, and nothing is kept -- a played lesson and the desktop preview.
 */
export class LocalFurnishing implements FurnishingLink {
  static readonly SELF = 'local';
  readonly self = LocalFurnishing.SELF;
  readonly #wire: FurnitureWire;

  constructor(
    pieces: readonly PieceInfo[],
    space: PlacementSpace,
    saved?: Readonly<Record<string, unknown>>,
  ) {
    const poses = posesFor(pieces, saved, space);
    this.#wire = {
      locked: false,
      rev: 0,
      pieces: pieces.map((piece) => ({ id: piece.id, ...poses[piece.id]!, heldBy: null })),
    };
  }

  grab(pieceId: string): Promise<GrabAnswer> {
    return Promise.resolve(
      this.#wire.pieces.some((piece) => piece.id === pieceId) ? 'granted' : 'no-furniture',
    );
  }

  /** Nothing to tell: the piece stands where the pupil put it. */
  hold(_pose: PosePayload): void {}

  drop(_pose: PosePayload): void {}

  onFurniture(handler: (wire: FurnitureWire) => void): void {
    handler(this.#wire);
  }

  /** Nobody else is here. */
  onMoves(_handler: (moves: MovesWire) => void): void {}

  dispose(): void {}
}
